import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Buildings,
  User,
  EnvelopeSimple,
  Phone,
  MapPin,
  Globe,
  IdentificationCard,
  Calendar,
  FloppyDisk,
  Plus,
  Trash,
  DownloadSimple,
  FilePdf,
  FileImage,
  FileXls,
  File as FileIcon,
  Paperclip,
} from "@phosphor-icons/react";

const DOC_CATEGORIES = [
  "Engagement Letter",
  "Prior Year Audit Report",
  "Trial Balance",
  "Bank Statements",
  "Tax Returns",
  "Org Chart",
  "Policies & Procedures",
  "Contracts",
  "Other",
];

function iconFor(ct, name) {
  const n = (name || "").toLowerCase();
  if ((ct || "").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(n)) return FileImage;
  if ((ct || "").includes("pdf") || n.endsWith(".pdf")) return FilePdf;
  if (n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".csv")) return FileXls;
  return FileIcon;
}

function fmtBytes(b) {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ClientProfileTab({ company, onCompanyUpdate, role }) {
  const isAudit = role === "audit_team";
  const [form, setForm] = useState({
    name: "",
    industry: "",
    address: "",
    phone: "",
    contact_name: "",
    contact_email: "",
    gst: "",
    pan: "",
    registration_no: "",
    fiscal_year_end: "",
    website: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!company) return;
    setForm({
      name: company.name || "",
      industry: company.industry || "",
      address: company.address || "",
      phone: company.phone || "",
      contact_name: company.contact_name || "",
      contact_email: company.contact_email || "",
      gst: company.gst || "",
      pan: company.pan || "",
      registration_no: company.registration_no || "",
      fiscal_year_end: company.fiscal_year_end || "",
      website: company.website || "",
      notes: company.notes || "",
    });
    setDirty(false);
  }, [company]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    if (!isAudit) return;
    setSaving(true);
    try {
      const r = await api.put(`/companies/${company.id}`, form);
      toast.success("Client profile saved");
      onCompanyUpdate?.(r.data);
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ------ Documents ------
  const fileRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState("Other");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploading, setUploading] = useState(false);

  const loadDocs = async () => {
    if (!company?.id) return;
    const r = await api.get(`/companies/${company.id}/documents`);
    setDocs(r.data);
  };
  useEffect(() => {
    loadDocs();
    // eslint-disable-next-line
  }, [company?.id]);

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      if (uploadCategory) fd.append("category", uploadCategory);
      if (uploadDescription) fd.append("description", uploadDescription);
      await api.post(`/companies/${company.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setUploadOpen(false);
      setUploadFile(null);
      setUploadDescription("");
      setUploadCategory("Other");
      loadDocs();
      window.dispatchEvent(new Event("activities-reload"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    await api.delete(`/attachments/${id}`);
    toast.success("Removed");
    loadDocs();
    window.dispatchEvent(new Event("activities-reload"));
  };

  const downloadDoc = async (att) => {
    try {
      const r = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: att.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) {
      toast.error("Download failed");
    }
  };

  // ------ Linked client users ------
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (!company?.id) return;
    api.get(`/companies/${company.id}/users`).then((r) => setUsers(r.data)).catch(() => {});
  }, [company?.id]);

  return (
    <div className="space-y-4" data-testid="client-profile-tab">
      {/* Client Info */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Geist" }}>
              <Buildings size={16} /> Client Profile
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Master record of this client engagement.</p>
          </div>
          {isAudit && (
            <Button onClick={save} disabled={!dirty || saving} className="bg-slate-900 hover:bg-slate-800" data-testid="save-client-profile-btn">
              <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldText
            label="Company Name"
            icon={<Buildings size={13} />}
            value={form.name}
            onChange={(v) => update("name", v)}
            disabled={!isAudit}
            testid="profile-name"
          />
          <FieldText
            label="Industry"
            value={form.industry}
            onChange={(v) => update("industry", v)}
            disabled={!isAudit}
            testid="profile-industry"
          />
          <FieldText
            label="Primary Contact Name"
            icon={<User size={13} />}
            value={form.contact_name}
            onChange={(v) => update("contact_name", v)}
            disabled={!isAudit}
            testid="profile-contact-name"
          />
          <FieldText
            label="Contact Email"
            icon={<EnvelopeSimple size={13} />}
            value={form.contact_email}
            onChange={(v) => update("contact_email", v)}
            disabled={!isAudit}
            testid="profile-contact-email"
          />
          <FieldText
            label="Phone"
            icon={<Phone size={13} />}
            value={form.phone}
            onChange={(v) => update("phone", v)}
            disabled={!isAudit}
            testid="profile-phone"
          />
          <FieldText
            label="Website"
            icon={<Globe size={13} />}
            value={form.website}
            onChange={(v) => update("website", v)}
            disabled={!isAudit}
            testid="profile-website"
            placeholder="https://"
          />
          <FieldText
            label="GSTIN"
            icon={<IdentificationCard size={13} />}
            value={form.gst}
            onChange={(v) => update("gst", v)}
            disabled={!isAudit}
            testid="profile-gst"
          />
          <FieldText
            label="PAN"
            icon={<IdentificationCard size={13} />}
            value={form.pan}
            onChange={(v) => update("pan", v)}
            disabled={!isAudit}
            testid="profile-pan"
          />
          <FieldText
            label="Registration / CIN No."
            value={form.registration_no}
            onChange={(v) => update("registration_no", v)}
            disabled={!isAudit}
            testid="profile-registration"
          />
          <FieldText
            label="Fiscal Year End"
            icon={<Calendar size={13} />}
            value={form.fiscal_year_end}
            onChange={(v) => update("fiscal_year_end", v)}
            disabled={!isAudit}
            testid="profile-fiscal-year"
            placeholder="31 March"
          />
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-500 flex items-center gap-1.5"><MapPin size={13} /> Registered Address</Label>
            <Textarea
              rows={2}
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              disabled={!isAudit}
              data-testid="profile-address"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-500">Notes / Engagement Scope</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              disabled={!isAudit}
              placeholder="Engagement scope, key contacts at the audit firm, special instructions..."
              data-testid="profile-notes"
            />
          </div>
        </div>
      </div>

      {/* Linked Users */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Geist" }}>
            <User size={16} /> Linked Client Users {users.length > 0 && <span className="text-slate-400 text-sm font-normal">({users.length})</span>}
          </h3>
          {isAudit && (
            <a href="/audit/users" className="text-xs text-blue-600 hover:underline">Manage users →</a>
          )}
        </div>
        {users.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center" data-testid="no-linked-users">
            No client users linked to this company yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2">Name</th>
                  <th className="text-left py-2 px-2">Email</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`linked-user-${u.id}`}>
                    <td className="py-2 px-2 font-medium">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
                          {u.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        {u.name}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-slate-600">
                      <div className="inline-flex items-center gap-2">
                        <EnvelopeSimple size={13} className="text-slate-400" />
                        <a href={`mailto:${u.email}`} className="hover:underline">{u.email}</a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Geist" }}>
              <Paperclip size={16} /> Client Documents
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Engagement letters, prior reports, policies & evidence library.</p>
          </div>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="upload-doc-btn">
                <Plus size={14} className="mr-1" /> Upload
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="upload-doc-dialog" aria-describedby="upload-doc-desc">
              <DialogHeader>
                <DialogTitle>Upload client document</DialogTitle>
                <p id="upload-doc-desc" className="text-xs text-slate-500">Add engagement letters, prior reports, policies and other client artefacts.</p>
              </DialogHeader>
              <form onSubmit={submitUpload} className="space-y-3">
                <div>
                  <Label>File (max 10 MB)</Label>
                  <input
                    ref={fileRef}
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800"
                    data-testid="upload-doc-file"
                    required
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={uploadCategory} onValueChange={setUploadCategory}>
                    <SelectTrigger data-testid="upload-doc-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Textarea
                    rows={2}
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    placeholder="Q4 FY26 engagement letter signed on 10-Apr"
                    data-testid="upload-doc-description"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={!uploadFile || uploading} data-testid="upload-doc-submit">
                    {uploading ? "Uploading..." : "Upload"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {docs.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-md p-8 text-center" data-testid="no-documents">
            <Paperclip size={28} className="mx-auto text-slate-400" />
            <p className="text-sm text-slate-600 mt-2 font-medium">No documents uploaded yet</p>
            <p className="text-xs text-slate-500 mt-1">Engagement letters, prior audit reports, trial balances, etc.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="documents-grid">
            {docs.map((d) => {
              const Icon = iconFor(d.content_type, d.original_filename);
              return (
                <div
                  key={d.id}
                  className="border border-slate-200 rounded-md p-3 hover:border-blue-300 transition flex items-start gap-3"
                  data-testid={`document-${d.id}`}
                >
                  <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                    <Icon size={20} className="text-slate-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" title={d.original_filename}>{d.original_filename}</div>
                    {d.category && (
                      <span className="inline-block text-[10px] uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded mt-1">{d.category}</span>
                    )}
                    {d.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{d.description}</div>}
                    <div className="text-[10px] text-slate-500 mt-1">
                      {fmtBytes(d.size)} · {new Date(d.created_at).toLocaleDateString()}
                    </div>
                    <div className="mt-2 flex items-center gap-1">
                      <button
                        onClick={() => downloadDoc(d)}
                        className="p-1 hover:bg-slate-100 rounded text-slate-600"
                        title="Download"
                        data-testid={`download-doc-${d.id}`}
                      >
                        <DownloadSimple size={14} />
                      </button>
                      {(isAudit || d.uploaded_by_role === role) && (
                        <button
                          onClick={() => removeDoc(d.id)}
                          className="p-1 hover:bg-red-50 text-red-500 rounded"
                          title="Remove"
                          data-testid={`delete-doc-${d.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function FieldText({ label, icon, value, onChange, disabled, testid, placeholder }) {
  return (
    <div>
      <Label className="text-xs text-slate-500 flex items-center gap-1.5">
        {icon} {label}
      </Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}
